import React, { useEffect, useMemo, useState } from 'react';
import { normalizeCompanyName } from 'utils/companyNormalizer';
import { buscarRecebimentosPorAction } from 'features/financeiro/services/financeiroService';
import FinanceiroGrafico from './FinanceiroGrafico';

const MONTH_LABELS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const formatBRL = (value) =>
  `R$ ${(parseFloat(value) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

const formatCompactBRL = (value) =>
  `R$ ${(parseFloat(value) || 0).toLocaleString('pt-BR', { notation: 'compact', maximumFractionDigits: 1 })}`;

const getCurrentMonthKey = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const getPreviousMonthKey = () => {
  const now = new Date();
  now.setMonth(now.getMonth() - 1);
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const normalizeMes = (mes) => {
  if (!mes) return '';
  const str = String(mes);
  if (/^\d{4}-\d{2}$/.test(str)) return str;
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str.slice(0, 7);
  return str;
};

const formatMesLabel = (mes) => {
  const normalizado = normalizeMes(mes);
  if (!/^\d{4}-\d{2}$/.test(normalizado)) return String(mes || '');
  const [ano, mesNumero] = normalizado.split('-');
  return `${mesNumero}/${ano}`;
};

const toArray = (value) => (Array.isArray(value) ? value : []);

const getParcelamentoOpenInfo = (parcelamento, currentMonthKey) => {
  const previousMonthKey = getPreviousMonthKey();
  const parcelasListadas = (parcelamento?.parcelas || []).filter((parcela) => !Boolean(parcela?.projetado));
  const totalParcelas = Math.max(Number(parcelamento?.total_parcelas) || 0, parcelasListadas.length);
  const faltantes = totalParcelas - parcelasListadas.length;
  if (faltantes <= 0) return { valido: false, faltantes: 0, valorFaltante: 0, valorUltimaParcela: 0 };

  const mesesListadosOrdenados = parcelasListadas
    .map((parcela) => normalizeMes(parcela?.mes))
    .filter((mes) => /^\d{4}-\d{2}$/.test(mes))
    .sort();
  const ultimaParcelaMes = mesesListadosOrdenados[mesesListadosOrdenados.length - 1];
  if (!ultimaParcelaMes || ultimaParcelaMes > currentMonthKey || ultimaParcelaMes < previousMonthKey) {
    return { valido: false, faltantes: 0, valorFaltante: 0, valorUltimaParcela: 0 };
  }

  const valorTotal = parseFloat(parcelamento?.valor_total) || 0;
  const valorListadas = parcelasListadas.reduce((acc, parcela) => acc + (parseFloat(parcela?.valor) || 0), 0);
  const valorFaltante = Math.max(valorTotal - valorListadas, 0);
  const parcelasOrdenadas = parcelasListadas
    .slice()
    .sort((a, b) => {
      const mesA = normalizeMes(a?.mes);
      const mesB = normalizeMes(b?.mes);
      if (mesA !== mesB) return mesA.localeCompare(mesB);
      return Number(a?.parcela || 0) - Number(b?.parcela || 0);
    });
  const ultimaParcela = parcelasOrdenadas[parcelasOrdenadas.length - 1];
  const valorUltimaParcela = parseFloat(ultimaParcela?.valor ?? 0) || 0;

  return { valido: true, faltantes, valorFaltante, valorUltimaParcela };
};

const normalizeParcela = (parcela, index) => ({
  parcela: Number(parcela?.parcela ?? parcela?.numero ?? index + 1),
  mes: normalizeMes(parcela?.mes ?? parcela?.competencia ?? parcela?.data ?? ''),
  valor: parseFloat(parcela?.valor ?? parcela?.valor_parcela ?? 0) || 0,
  projetado: Boolean(parcela?.projetado ?? parcela?.is_projetado ?? false)
});

const resolveCategoria = (metadata) => {
  const categoriaDireta = metadata?.categoria || metadata?.category || metadata?.grupo || metadata?.conta || '';
  if (String(categoriaDireta).trim()) return String(categoriaDireta).trim();

  const id = String(metadata?.parcelamento_id || '');
  if (id.includes('|')) {
    const partes = id.split('|').map((parte) => String(parte).trim()).filter(Boolean);
    if (partes.length > 0) return partes[partes.length - 1];
  }

  return 'Sem categoria';
};

const normalizeParcelamento = (entry) => {
  if (!entry || typeof entry !== 'object') return null;

  const metadata = (!entry.parcelamento_id && !entry.descricao && Array.isArray(entry.parcelamentos) && entry.parcelamentos[0])
    ? entry.parcelamentos[0]
    : entry;

  const parcelasRaw = toArray(entry.parcelas).length > 0
    ? toArray(entry.parcelas)
    : (toArray(entry.fluxo_parcelas).length > 0
        ? toArray(entry.fluxo_parcelas)
        : toArray(metadata?.parcelas));

  const parcelas = parcelasRaw.map(normalizeParcela);

  const parcelamentoId = metadata?.parcelamento_id || metadata?.id || '';
  const descricao = metadata?.descricao || metadata?.nome || '';

  if (!parcelamentoId && !descricao && parcelas.length === 0) return null;

  return {
    parcelamento_id: parcelamentoId || descricao || `parcelamento-${parcelas.length}`,
    descricao: descricao || parcelamentoId || 'Sem descrição',
    categoria: resolveCategoria(metadata),
    total_parcelas: Number(metadata?.total_parcelas) || parcelas.length,
    valor_total: parseFloat(metadata?.valor_total ?? 0) || parcelas.reduce((acc, p) => acc + (p.valor || 0), 0),
    parcelas
  };
};

const extractFluxoParcelas = (payload) => {
  const fluxoMes = new Map();

  const visit = (node) => {
    if (!node) return;
    if (Array.isArray(node)) {
      node.forEach(visit);
      return;
    }
    if (typeof node !== 'object') return;

    if (Array.isArray(node.fluxo_parcelas)) {
      node.fluxo_parcelas.forEach((item) => {
        const mes = normalizeMes(item?.mes);
        if (!mes) return;
        const valor = parseFloat(item?.valor ?? 0) || 0;
        fluxoMes.set(mes, (fluxoMes.get(mes) || 0) + valor);
      });
    }

    Object.values(node).forEach(visit);
  };

  visit(payload);

  return Array.from(fluxoMes.entries())
    .map(([mes, valor]) => ({ mes, valor }))
    .sort((a, b) => {
      const dateA = new Date(`${a.mes}-01`);
      const dateB = new Date(`${b.mes}-01`);
      return dateA - dateB;
    });
};

const extractParcelamentos = (payload) => {
  const candidates = [];

  const visit = (node) => {
    if (!node) return;
    if (Array.isArray(node)) {
      node.forEach(visit);
      return;
    }
    if (typeof node !== 'object') return;

    if (
      node.parcelamento_id ||
      node.descricao ||
      Array.isArray(node.parcelas) ||
      (Array.isArray(node.fluxo_parcelas) && (node.parcelamento_id || node.descricao || node.id))
    ) {
      candidates.push(node);
    }

    Object.values(node).forEach(visit);
  };

  visit(payload);

  const normalized = candidates.map(normalizeParcelamento).filter(Boolean);
  const dedup = new Map();
  normalized.forEach((item) => {
    dedup.set(item.parcelamento_id, item);
  });

  return Array.from(dedup.values());
};

const ParcelamentosMovfit = ({ empresaAtual }) => {
  const isMovFit = normalizeCompanyName(empresaAtual) === 'mov fit';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [parcelamentos, setParcelamentos] = useState([]);
  const [fluxoParcelas, setFluxoParcelas] = useState([]);
  const [contentMode, setContentMode] = useState('tabela');
  const [viewMode, setViewMode] = useState('mes');
  const [graphViewMode, setGraphViewMode] = useState('heatmap');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isContentDropdownOpen, setIsContentDropdownOpen] = useState(false);
  const [isGraphDropdownOpen, setIsGraphDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCardFilter, setActiveCardFilter] = useState('all');

  useEffect(() => {
    let active = true;

    const carregar = async () => {
      if (!isMovFit) return;
      setLoading(true);
      setError(null);

      try {
        const response = await buscarRecebimentosPorAction('parcelados', { empresa: empresaAtual });
        if (!active) return;

        const lista = extractParcelamentos(response?.data);
        const fluxo = extractFluxoParcelas(response?.data);
        setParcelamentos(lista);
        setFluxoParcelas(fluxo);
      } catch (requestError) {
        if (!active) return;
        setError('Não foi possível carregar os parcelamentos.');
        setParcelamentos([]);
        setFluxoParcelas([]);
      } finally {
        if (active) setLoading(false);
      }
    };

    carregar();
    return () => {
      active = false;
    };
  }, [empresaAtual, isMovFit]);

  const filteredParcelamentos = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return parcelamentos;

    return (parcelamentos || []).filter((item) => {
      const descricao = String(item?.descricao || '').toLowerCase();
      const id = String(item?.parcelamento_id || '').toLowerCase();

      if (descricao.includes(term) || id.includes(term)) return true;

      return (item?.parcelas || []).some((parcela) => {
        const mes = String(normalizeMes(parcela?.mes)).toLowerCase();
        const valor = String(parcela?.valor ?? '').toLowerCase();
        const numero = String(parcela?.parcela ?? '').toLowerCase();
        const categoria = String(item?.categoria || '').toLowerCase();
        return mes.includes(term) || valor.includes(term) || numero.includes(term) || categoria.includes(term);
      });
    });
  }, [parcelamentos, searchTerm]);

  const colunasParcelas = useMemo(() => {
    const parcelasSet = new Set();

    (filteredParcelamentos || []).forEach((item) => {
      (item?.parcelas || []).forEach((parcela) => {
        const numero = Number(parcela?.parcela);
        if (Number.isFinite(numero) && numero > 0) {
          parcelasSet.add(numero);
        }
      });
    });

    return Array.from(parcelasSet).sort((a, b) => a - b);
  }, [filteredParcelamentos]);

  const colunasMeses = useMemo(() => {
    const meses = new Set();
    (filteredParcelamentos || []).forEach((item) => {
      (item?.parcelas || []).forEach((parcela) => {
        const mes = normalizeMes(parcela?.mes);
        if (mes) meses.add(mes);
      });
    });

    return Array.from(meses).sort((a, b) => {
      const dateA = new Date(`${a}-01`);
      const dateB = new Date(`${b}-01`);
      return dateA - dateB;
    });
  }, [filteredParcelamentos]);

  const cardsResumoTabela = useMemo(() => {
    const resumo = {
      contasUmaParcela: 0,
      contasDuasACinco: 0,
      contasMaisDeCinco: 0,
      valorTotalUmaParcelaFaltante: 0
    };

    const currentMonthKey = getCurrentMonthKey();

    (filteredParcelamentos || []).forEach((parcelamento) => {
      const info = getParcelamentoOpenInfo(parcelamento, currentMonthKey);
      if (!info.valido) return;
      const faltantes = info.faltantes;

      if (faltantes === 1) resumo.contasUmaParcela += 1;
      if (faltantes >= 2 && faltantes <= 5) resumo.contasDuasACinco += 1;
      if (faltantes > 5) resumo.contasMaisDeCinco += 1;

      if (faltantes === 1) resumo.valorTotalUmaParcelaFaltante += info.valorUltimaParcela;
    });

    return resumo;
  }, [filteredParcelamentos]);

  const parcelamentosFiltradosPorCard = useMemo(() => {
    if (activeCardFilter === 'all') return filteredParcelamentos;
    const currentMonthKey = getCurrentMonthKey();

    return (filteredParcelamentos || []).filter((parcelamento) => {
      const info = getParcelamentoOpenInfo(parcelamento, currentMonthKey);
      if (!info.valido) return false;

      if (activeCardFilter === 'faltando-1') return info.faltantes === 1;
      if (activeCardFilter === 'faltando-2-5') return info.faltantes >= 2 && info.faltantes <= 5;
      if (activeCardFilter === 'faltando-6-plus') return info.faltantes > 5;
      if (activeCardFilter === 'valor-1-faltante') return info.faltantes === 1;
      return true;
    });
  }, [activeCardFilter, filteredParcelamentos]);

  const colunasMesesCategoria = useMemo(() => {
    const meses = new Set();
    (parcelamentosFiltradosPorCard || []).forEach((item) => {
      (item?.parcelas || []).forEach((parcela) => {
        const mes = normalizeMes(parcela?.mes);
        if (mes) meses.add(mes);
      });
    });

    return Array.from(meses).sort((a, b) => {
      const dateA = new Date(`${a}-01`);
      const dateB = new Date(`${b}-01`);
      return dateA - dateB;
    });
  }, [parcelamentosFiltradosPorCard]);

  const colunasCategorias = useMemo(() => {
    const categorias = new Set();
    (parcelamentosFiltradosPorCard || []).forEach((item) => {
      categorias.add(item?.categoria || 'Sem categoria');
    });
    return Array.from(categorias).sort((a, b) => String(a).localeCompare(String(b)));
  }, [parcelamentosFiltradosPorCard]);

  const matrizCategoriaPorMes = useMemo(() => {
    const matriz = new Map();
    (parcelamentosFiltradosPorCard || []).forEach((parcelamento) => {
      const categoria = parcelamento?.categoria || 'Sem categoria';
      (parcelamento?.parcelas || []).forEach((parcela) => {
        const mes = normalizeMes(parcela?.mes);
        if (!mes) return;
        const key = `${mes}__${categoria}`;
        const atual = matriz.get(key) || 0;
        matriz.set(key, atual + (parseFloat(parcela?.valor) || 0));
      });
    });
    return matriz;
  }, [parcelamentosFiltradosPorCard]);

  const totaisCategoria = useMemo(() => {
    const totalPorCategoria = new Map();
    let totalGeral = 0;

    colunasCategorias.forEach((categoria) => {
      let soma = 0;
      colunasMesesCategoria.forEach((mes) => {
        soma += matrizCategoriaPorMes.get(`${mes}__${categoria}`) || 0;
      });
      totalPorCategoria.set(categoria, soma);
      totalGeral += soma;
    });

    return { totalPorCategoria, totalGeral };
  }, [colunasCategorias, colunasMesesCategoria, matrizCategoriaPorMes]);

  const dadosGraficoFluxoParcelas = useMemo(() => {
    if (!Array.isArray(fluxoParcelas) || fluxoParcelas.length === 0) return null;

    const labels = MONTH_LABELS;
    const anos = Array.from(
      new Set(
        fluxoParcelas
          .map((item) => String(item?.mes || '').slice(0, 4))
          .filter((ano) => /^\d{4}$/.test(ano))
      )
    ).sort((a, b) => Number(a) - Number(b));

    const palette = ['#22c55e', '#3b82f6', '#a855f7', '#ef4444', '#14b8a6', '#f59e0b', '#ec4899'];

    const datasets = anos.map((ano, index) => {
      const valoresPorMes = new Array(12).fill(0);

      fluxoParcelas.forEach((item) => {
        const mes = normalizeMes(item?.mes);
        const [itemAno, itemMes] = String(mes).split('-');
        if (itemAno !== ano) return;
        const monthIndex = Number(itemMes) - 1;
        if (monthIndex < 0 || monthIndex > 11) return;
        valoresPorMes[monthIndex] += parseFloat(item?.valor ?? 0) || 0;
      });

      const color = palette[index % palette.length];
      return {
        label: ano,
        data: valoresPorMes,
        borderColor: color,
        backgroundColor: color,
        borderWidth: 2,
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 5
      };
    });

    return {
      labels,
      datasets,
      formatoMoeda: true,
      mostrarLegenda: true
    };
  }, [fluxoParcelas]);

  const dadosGraficoFluxoParcelasBarras = useMemo(() => {
    if (!dadosGraficoFluxoParcelas) return null;

    return {
      ...dadosGraficoFluxoParcelas,
      datasets: (dadosGraficoFluxoParcelas.datasets || []).map((dataset) => ({
        ...dataset,
        borderWidth: 1,
        borderRadius: 4,
        backgroundColor: `${dataset.backgroundColor}66`,
        borderColor: dataset.borderColor
      }))
    };
  }, [dadosGraficoFluxoParcelas]);

  const heatmapData = useMemo(() => {
    if (!Array.isArray(fluxoParcelas) || fluxoParcelas.length === 0) return null;

    const anos = Array.from(
      new Set(
        fluxoParcelas
          .map((item) => String(item?.mes || '').slice(0, 4))
          .filter((ano) => /^\d{4}$/.test(ano))
      )
    ).sort((a, b) => Number(a) - Number(b));

    const matrix = anos.map((ano) => ({
      ano,
      valores: new Array(12).fill(0)
    }));

    const indexByAno = new Map(matrix.map((item, index) => [item.ano, index]));

    fluxoParcelas.forEach((item) => {
      const mes = normalizeMes(item?.mes);
      const [ano, mesNumero] = String(mes).split('-');
      const yearIndex = indexByAno.get(ano);
      const monthIndex = Number(mesNumero) - 1;
      if (yearIndex === undefined || monthIndex < 0 || monthIndex > 11) return;
      matrix[yearIndex].valores[monthIndex] += parseFloat(item?.valor ?? 0) || 0;
    });

    const allValues = matrix.flatMap((item) => item.valores).filter((value) => Number.isFinite(value) && value > 0);
    const maxValue = allValues.length > 0 ? Math.max(...allValues) : 0;
    const minValue = allValues.length > 0 ? Math.min(...allValues) : 0;

    return {
      months: MONTH_LABELS,
      matrix,
      maxValue,
      minValue
    };
  }, [fluxoParcelas]);

  if (!isMovFit) return null;

  return (
    <div className="bg-[color:var(--surface)] rounded-lg border border-[color:var(--border)] overflow-hidden">
      <div className="px-4 sm:px-6 py-4 border-b border-[color:var(--border)]">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-[color:var(--text-primary)]">Parcelamentos MOV Fit</h3>
            <p className="text-xs sm:text-sm text-[color:var(--text-muted)] mt-1">Cronograma de parcelamentos financeiros</p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
            <div className="relative w-full sm:w-96">
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Buscar por descrição, categoria ou ID..."
                className="w-full pl-9 pr-3 py-2 text-xs sm:text-sm border border-[color:var(--border)] rounded-lg bg-[color:var(--surface)] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:border-[color:var(--accent)]"
              />
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[color:var(--text-muted)]"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 014.375 8.836l2.644 2.644a.75.75 0 11-1.06 1.06l-2.644-2.644A5.5 5.5 0 119 3.5zm0 1.5a4 4 0 100 8 4 4 0 000-8z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <span className="text-xs sm:text-sm text-[color:var(--text-muted)]">Seção:</span>
              <div className="relative w-full sm:w-40">
                <button
                  type="button"
                  onClick={() => setIsContentDropdownOpen((state) => !state)}
                  className="w-full flex items-center justify-between px-3 py-2 text-xs sm:text-sm border border-[color:var(--border)] rounded-lg bg-[color:var(--surface)] hover:bg-[color:var(--surface-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:border-[color:var(--accent)]"
                >
                  <span>{contentMode === 'grafico' ? 'Fluxo' : 'Tabela'}</span>
                  <svg className="h-3 w-3 sm:h-4 sm:w-4 text-[color:var(--text-muted)] flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.25 8.29a.75.75 0 01-.02-1.08z" clipRule="evenodd" />
                  </svg>
                </button>

                {isContentDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setIsContentDropdownOpen(false)} />
                    <div className="absolute z-50 mt-1 w-full bg-[color:var(--surface)] rounded-lg shadow-lg border border-[color:var(--border)] overflow-hidden">
                      <button
                        type="button"
                        onClick={() => {
                          setContentMode('tabela');
                          setIsContentDropdownOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs sm:text-sm hover:bg-[color:var(--surface-muted)] transition-colors ${
                          contentMode === 'tabela' ? 'bg-[color:var(--surface-muted)] text-[color:var(--accent)]' : 'text-[color:var(--text-primary)]'
                        }`}
                      >
                        Tabela
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setContentMode('grafico');
                          setIsContentDropdownOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs sm:text-sm hover:bg-[color:var(--surface-muted)] transition-colors ${
                          contentMode === 'grafico' ? 'bg-[color:var(--surface-muted)] text-[color:var(--accent)]' : 'text-[color:var(--text-primary)]'
                        }`}
                      >
                        Fluxo
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
            {contentMode === 'tabela' && (
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <span className="text-xs sm:text-sm text-[color:var(--text-muted)]">Visualização:</span>
                <div className="relative w-full sm:w-40">
                <button
                  type="button"
                  onClick={() => setIsDropdownOpen((state) => !state)}
                  className="w-full flex items-center justify-between px-3 py-2 text-xs sm:text-sm border border-[color:var(--border)] rounded-lg bg-[color:var(--surface)] hover:bg-[color:var(--surface-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:border-[color:var(--accent)]"
                >
                  <span>{viewMode === 'parcelas' ? 'Por Parcelas' : viewMode === 'categoria' ? 'Por Categoria' : 'Por Mês'}</span>
                  <svg className="h-3 w-3 sm:h-4 sm:w-4 text-[color:var(--text-muted)] flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.25 8.29a.75.75 0 01-.02-1.08z" clipRule="evenodd" />
                  </svg>
                </button>

                {isDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setIsDropdownOpen(false)} />
                    <div className="absolute z-50 mt-1 w-full bg-[color:var(--surface)] rounded-lg shadow-lg border border-[color:var(--border)] overflow-hidden">
                      <button
                        type="button"
                        onClick={() => {
                          setViewMode('parcelas');
                          setIsDropdownOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs sm:text-sm hover:bg-[color:var(--surface-muted)] transition-colors ${
                          viewMode === 'parcelas' ? 'bg-[color:var(--surface-muted)] text-[color:var(--accent)]' : 'text-[color:var(--text-primary)]'
                        }`}
                      >
                        Por Parcelas
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setViewMode('mes');
                          setIsDropdownOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs sm:text-sm hover:bg-[color:var(--surface-muted)] transition-colors ${
                          viewMode === 'mes' ? 'bg-[color:var(--surface-muted)] text-[color:var(--accent)]' : 'text-[color:var(--text-primary)]'
                        }`}
                      >
                        Por Mês
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setViewMode('categoria');
                          setIsDropdownOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs sm:text-sm hover:bg-[color:var(--surface-muted)] transition-colors ${
                          viewMode === 'categoria' ? 'bg-[color:var(--surface-muted)] text-[color:var(--accent)]' : 'text-[color:var(--text-primary)]'
                        }`}
                      >
                        Por Categoria
                      </button>
                    </div>
                  </>
                )}
                </div>
              </div>
            )}
            {contentMode === 'grafico' && (
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <span className="text-xs sm:text-sm text-[color:var(--text-muted)]">Visualização:</span>
                <div className="relative w-full sm:w-40">
                  <button
                    type="button"
                    onClick={() => setIsGraphDropdownOpen((state) => !state)}
                    className="w-full flex items-center justify-between px-3 py-2 text-xs sm:text-sm border border-[color:var(--border)] rounded-lg bg-[color:var(--surface)] hover:bg-[color:var(--surface-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:border-[color:var(--accent)]"
                  >
                    <span>{graphViewMode === 'heatmap' ? 'Heatmap' : graphViewMode === 'line' ? 'Linha' : 'Barras'}</span>
                    <svg className="h-3 w-3 sm:h-4 sm:w-4 text-[color:var(--text-muted)] flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.25 8.29a.75.75 0 01-.02-1.08z" clipRule="evenodd" />
                    </svg>
                  </button>

                  {isGraphDropdownOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setIsGraphDropdownOpen(false)} />
                      <div className="absolute z-50 mt-1 w-full bg-[color:var(--surface)] rounded-lg shadow-lg border border-[color:var(--border)] overflow-hidden">
                        <button
                          type="button"
                          onClick={() => {
                            setGraphViewMode('heatmap');
                            setIsGraphDropdownOpen(false);
                          }}
                          className={`w-full text-left px-3 py-2 text-xs sm:text-sm hover:bg-[color:var(--surface-muted)] transition-colors ${
                            graphViewMode === 'heatmap' ? 'bg-[color:var(--surface-muted)] text-[color:var(--accent)]' : 'text-[color:var(--text-primary)]'
                          }`}
                        >
                          Heatmap
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setGraphViewMode('line');
                            setIsGraphDropdownOpen(false);
                          }}
                          className={`w-full text-left px-3 py-2 text-xs sm:text-sm hover:bg-[color:var(--surface-muted)] transition-colors ${
                            graphViewMode === 'line' ? 'bg-[color:var(--surface-muted)] text-[color:var(--accent)]' : 'text-[color:var(--text-primary)]'
                          }`}
                        >
                          Linha
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setGraphViewMode('bar');
                            setIsGraphDropdownOpen(false);
                          }}
                          className={`w-full text-left px-3 py-2 text-xs sm:text-sm hover:bg-[color:var(--surface-muted)] transition-colors ${
                            graphViewMode === 'bar' ? 'bg-[color:var(--surface-muted)] text-[color:var(--accent)]' : 'text-[color:var(--text-primary)]'
                          }`}
                        >
                          Barras
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[color:var(--accent)]" />
          </div>
        ) : error ? (
          <div className="text-sm text-red-500 py-6">{error}</div>
        ) : (
          <div className="space-y-6">
            {contentMode === 'grafico' ? (
              dadosGraficoFluxoParcelas ? (
                <div className="overflow-hidden">
                  <h4 className="text-sm sm:text-base font-semibold text-[color:var(--text-primary)] mb-2">Fluxo de Parcelamentos</h4>
                  <p className="text-xs sm:text-sm text-[color:var(--text-muted)] mb-4">Evolução mensal dos parcelamentos por ano</p>
                  {graphViewMode === 'line' ? (
                    <FinanceiroGrafico
                      tipo="line"
                      dados={dadosGraficoFluxoParcelas}
                      mostrarTitulo={false}
                      className="!p-0 border-0 bg-transparent"
                    />
                  ) : graphViewMode === 'bar' ? (
                    <FinanceiroGrafico
                      tipo="bar"
                      dados={dadosGraficoFluxoParcelasBarras}
                      mostrarTitulo={false}
                      className="!p-0 border-0 bg-transparent"
                    />
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center justify-end gap-3 text-[11px] sm:text-xs text-[color:var(--text-muted)]">
                        <span>{heatmapData?.minValue > 0 ? formatCompactBRL(heatmapData.minValue) : 'R$ 0'} (menor)</span>
                        <div className="w-28 h-2 rounded-full bg-gradient-to-r from-emerald-500/10 to-emerald-500/90 border border-emerald-400/20" />
                        <span>{formatCompactBRL(heatmapData?.maxValue || 0)} (maior)</span>
                      </div>
                      <div className="overflow-auto rounded-md border border-[color:var(--border)]">
                        <table className="w-full min-w-[760px]">
                          <thead className="bg-[color:var(--surface-muted)] sticky top-0 z-10">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs sm:text-sm font-medium text-[color:var(--text-muted)] uppercase tracking-wider sticky left-0 z-20 bg-[color:var(--surface-muted)]">
                                Ano
                              </th>
                              {heatmapData?.months.map((month) => (
                                <th
                                  key={`heatmap-header-${month}`}
                                  className="px-3 py-2 text-center text-xs sm:text-sm font-medium text-[color:var(--text-muted)] uppercase tracking-wider"
                                >
                                  {month}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-[color:var(--border)]">
                            {heatmapData?.matrix.map((row) => (
                              <tr key={`heatmap-row-${row.ano}`}>
                                <td className="px-3 py-2 text-xs sm:text-sm font-semibold text-[color:var(--text-primary)] sticky left-0 z-10 bg-[color:var(--surface)]">
                                  {row.ano}
                                </td>
                                {row.valores.map((value, monthIndex) => {
                                  const ratio = heatmapData.maxValue > 0 ? value / heatmapData.maxValue : 0;
                                  const alpha = value > 0 ? 0.12 + ratio * 0.78 : 0.03;
                                  const backgroundColor = `rgba(16, 185, 129, ${alpha.toFixed(3)})`;

                                  return (
                                    <td
                                      key={`heatmap-${row.ano}-${monthIndex}`}
                                      className="px-2 py-2 text-center text-[10px] sm:text-xs text-[color:var(--text-primary)]"
                                      style={{ backgroundColor }}
                                      title={`${MONTH_LABELS[monthIndex]}/${row.ano}: ${formatBRL(value)}`}
                                    >
                                      {value > 0 ? formatCompactBRL(value) : '-'}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-[color:var(--text-muted)] py-2">Sem dados para o gráfico de fluxo.</div>
              )
            ) : (
              filteredParcelamentos.length === 0 ? (
                <div className="text-sm text-[color:var(--text-muted)] py-2">Nenhum parcelamento encontrado.</div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                    <button
                      type="button"
                      onClick={() => setActiveCardFilter((prev) => (prev === 'faltando-1' ? 'all' : 'faltando-1'))}
                      className={`text-left rounded-lg border p-3 transition-colors ${
                        activeCardFilter === 'faltando-1'
                          ? 'border-[color:var(--accent)] bg-[color:var(--surface)]'
                          : 'border-[color:var(--border)] bg-[color:var(--surface-muted)]'
                      }`}
                    >
                      <p className="text-[11px] sm:text-xs uppercase tracking-wide text-[color:var(--text-muted)]">Contas com 1 parcela</p>
                      <p className="mt-2 text-xl sm:text-2xl font-semibold text-[color:var(--text-primary)]">{cardsResumoTabela.contasUmaParcela}</p>
                      <p className="text-[11px] sm:text-xs text-[color:var(--text-muted)] mt-1">
                        {activeCardFilter === 'faltando-1' ? 'Filtro ativo' : 'Em aberto'}
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveCardFilter((prev) => (prev === 'faltando-2-5' ? 'all' : 'faltando-2-5'))}
                      className={`text-left rounded-lg border p-3 transition-colors ${
                        activeCardFilter === 'faltando-2-5'
                          ? 'border-[color:var(--accent)] bg-[color:var(--surface)]'
                          : 'border-[color:var(--border)] bg-[color:var(--surface-muted)]'
                      }`}
                    >
                      <p className="text-[11px] sm:text-xs uppercase tracking-wide text-[color:var(--text-muted)]">Contas com 2 a 5 parcelas</p>
                      <p className="mt-2 text-xl sm:text-2xl font-semibold text-[color:var(--text-primary)]">{cardsResumoTabela.contasDuasACinco}</p>
                      <p className="text-[11px] sm:text-xs text-[color:var(--text-muted)] mt-1">
                        {activeCardFilter === 'faltando-2-5' ? 'Filtro ativo' : 'Em aberto'}
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveCardFilter((prev) => (prev === 'faltando-6-plus' ? 'all' : 'faltando-6-plus'))}
                      className={`text-left rounded-lg border p-3 transition-colors ${
                        activeCardFilter === 'faltando-6-plus'
                          ? 'border-[color:var(--accent)] bg-[color:var(--surface)]'
                          : 'border-[color:var(--border)] bg-[color:var(--surface-muted)]'
                      }`}
                    >
                      <p className="text-[11px] sm:text-xs uppercase tracking-wide text-[color:var(--text-muted)]">Contas com 6+ parcelas</p>
                      <p className="mt-2 text-xl sm:text-2xl font-semibold text-[color:var(--text-primary)]">{cardsResumoTabela.contasMaisDeCinco}</p>
                      <p className="text-[11px] sm:text-xs text-[color:var(--text-muted)] mt-1">
                        {activeCardFilter === 'faltando-6-plus' ? 'Filtro ativo' : 'Em aberto'}
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveCardFilter((prev) => (prev === 'valor-1-faltante' ? 'all' : 'valor-1-faltante'))}
                      className={`text-left rounded-lg border p-3 transition-colors ${
                        activeCardFilter === 'valor-1-faltante'
                          ? 'border-[color:var(--accent)] bg-[color:var(--surface)]'
                          : 'border-[color:var(--border)] bg-[color:var(--surface-muted)]'
                      }`}
                    >
                      <p className="text-[11px] sm:text-xs uppercase tracking-wide text-[color:var(--text-muted)]">Valor total (1 parcela faltante)</p>
                      <p className="mt-2 text-xl sm:text-2xl font-semibold text-[color:var(--text-primary)]">{formatBRL(cardsResumoTabela.valorTotalUmaParcelaFaltante)}</p>
                      <p className="text-[11px] sm:text-xs text-[color:var(--text-muted)] mt-1">
                        {activeCardFilter === 'valor-1-faltante' ? 'Filtro ativo' : 'Somente contas com 1 faltante'}
                      </p>
                    </button>
                  </div>
                  {activeCardFilter !== 'all' && (
                    <div className="flex items-center justify-between text-xs sm:text-sm text-[color:var(--text-muted)] px-1">
                      <span>{parcelamentosFiltradosPorCard.length} conta(s) no filtro atual</span>
                      <button
                        type="button"
                        onClick={() => setActiveCardFilter('all')}
                        className="text-[color:var(--accent)] hover:underline"
                      >
                        Limpar filtro
                      </button>
                    </div>
                  )}

                  <div className="max-h-[28rem] overflow-auto rounded-md border border-[color:var(--border)]">
                    <table className="w-full min-w-[720px] divide-y divide-[color:var(--border)]">
                      <thead className="bg-[color:var(--surface-muted)] sticky top-0 z-10">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs sm:text-sm font-medium text-[color:var(--text-muted)] uppercase tracking-wider sticky left-0 z-20 bg-[color:var(--surface-muted)]">
                            {viewMode === 'categoria' ? 'Mês' : 'Parcelamento'}
                          </th>
                          {viewMode !== 'categoria' && (
                            <th className="px-3 py-2 text-center text-xs sm:text-sm font-medium text-[color:var(--text-muted)] uppercase tracking-wider whitespace-nowrap">
                              Total Parcelas
                            </th>
                          )}
                          {viewMode === 'parcelas'
                            ? colunasParcelas.map((coluna) => (
                                <th
                                  key={`p-${coluna}`}
                                  className="px-3 py-2 text-right text-xs sm:text-sm font-medium text-[color:var(--text-muted)] uppercase tracking-wider whitespace-nowrap"
                                >
                                  P{coluna}
                                </th>
                              ))
                            : viewMode === 'categoria'
                              ? colunasCategorias.map((categoria) => (
                                  <th
                                    key={`cat-${categoria}`}
                                    className="px-3 py-2 text-right text-xs sm:text-sm font-medium text-[color:var(--text-muted)] uppercase tracking-wider whitespace-nowrap"
                                  >
                                    {categoria}
                                  </th>
                                ))
                              : colunasMeses.map((mes) => (
                                  <th
                                    key={`m-${mes}`}
                                    className="px-3 py-2 text-right text-xs sm:text-sm font-medium text-[color:var(--text-muted)] uppercase tracking-wider whitespace-nowrap"
                                  >
                                    {formatMesLabel(mes)}
                                  </th>
                                ))}
                          {viewMode === 'categoria' && (
                            <th className="px-3 py-2 text-right text-xs sm:text-sm font-medium text-[color:var(--text-muted)] uppercase tracking-wider whitespace-nowrap">
                              Total Mês
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="bg-[color:var(--surface)] divide-y divide-[color:var(--border)]">
                        {viewMode === 'categoria'
                          ? colunasMesesCategoria.map((mes) => (
                              <tr key={`row-mes-${mes}`}>
                                <td className="px-3 py-3 text-xs sm:text-sm text-[color:var(--text-primary)] max-w-[280px] sticky left-0 z-10 bg-[color:var(--surface)] whitespace-nowrap">
                                  <div className="font-medium whitespace-nowrap">{formatMesLabel(mes)}</div>
                                </td>
                                {colunasCategorias.map((categoria) => {
                                  const valor = matrizCategoriaPorMes.get(`${mes}__${categoria}`) || 0;
                                  return (
                                    <td
                                      key={`cell-${mes}-${categoria}`}
                                      className="px-3 py-3 text-right text-xs sm:text-sm whitespace-nowrap text-[color:var(--text-primary)]"
                                    >
                                      {valor > 0 ? formatBRL(valor) : '-'}
                                    </td>
                                  );
                                })}
                                <td className="px-3 py-3 text-right text-xs sm:text-sm whitespace-nowrap text-[color:var(--text-primary)] font-medium">
                                  {formatBRL(
                                    colunasCategorias.reduce(
                                      (acc, categoria) => acc + (matrizCategoriaPorMes.get(`${mes}__${categoria}`) || 0),
                                      0
                                    )
                                  )}
                                </td>
                              </tr>
                            ))
                          : parcelamentosFiltradosPorCard.map((parcelamento) => {
                          const descricao = parcelamento?.descricao || parcelamento?.parcelamento_id || 'Sem descrição';
                          const parcelas = Array.isArray(parcelamento?.parcelas) ? parcelamento.parcelas : [];
                          const totalParcelas = Math.max(Number(parcelamento?.total_parcelas) || 0, parcelas.length);

                          return (
                            <tr key={parcelamento?.parcelamento_id || descricao}>
                              <td className="px-3 py-3 text-xs sm:text-sm text-[color:var(--text-primary)] max-w-[280px] sticky left-0 z-10 bg-[color:var(--surface)]">
                                <div className="font-medium break-words">{descricao}</div>
                              </td>
                              <td className="px-3 py-3 text-center text-xs sm:text-sm text-[color:var(--text-primary)] whitespace-nowrap">
                                {totalParcelas}
                              </td>

                              {viewMode === 'parcelas'
                                ? colunasParcelas.map((numeroParcela) => {
                                    const parcelaAtual = parcelas.find((p) => Number(p?.parcela) === numeroParcela);

                                    if (!parcelaAtual) {
                                      return (
                                        <td
                                          key={`${descricao}-p-${numeroParcela}`}
                                          className="px-3 py-3 text-right text-xs sm:text-sm text-[color:var(--text-muted)]"
                                        >
                                          -
                                        </td>
                                      );
                                    }

                                    const isProjetado = Boolean(parcelaAtual?.projetado);
                                    return (
                                      <td
                                        key={`${descricao}-p-${numeroParcela}`}
                                        className={`px-3 py-3 text-right text-xs sm:text-sm whitespace-nowrap ${isProjetado ? 'opacity-60' : ''}`}
                                      >
                                        <span className="text-[color:var(--text-primary)]">{formatBRL(parcelaAtual?.valor)}</span>
                                        {isProjetado && (
                                          <span
                                            className="ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-amber-500/20 text-amber-300"
                                            title="Valor projetado"
                                          >
                                            Projetado
                                          </span>
                                        )}
                                      </td>
                                    );
                                  })
                                : colunasMeses.map((mes) => {
                                    const parcelasMes = parcelas.filter((p) => normalizeMes(p?.mes) === mes);
                                    if (parcelasMes.length === 0) {
                                      return (
                                        <td
                                          key={`${descricao}-m-${mes}`}
                                          className="px-3 py-3 text-right text-xs sm:text-sm text-[color:var(--text-muted)]"
                                        >
                                          -
                                        </td>
                                      );
                                    }

                                    const valorMes = parcelasMes.reduce((acc, p) => acc + (parseFloat(p?.valor) || 0), 0);
                                    const hasProjetado = parcelasMes.some((p) => Boolean(p?.projetado));

                                    return (
                                      <td
                                        key={`${descricao}-m-${mes}`}
                                        className={`px-3 py-3 text-right text-xs sm:text-sm whitespace-nowrap ${hasProjetado ? 'opacity-60' : ''}`}
                                      >
                                        <span className="text-[color:var(--text-primary)]">{formatBRL(valorMes)}</span>
                                        {hasProjetado && (
                                          <span
                                            className="ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-amber-500/20 text-amber-300"
                                            title="Inclui valor projetado"
                                          >
                                            Projetado
                                          </span>
                                        )}
                                      </td>
                                    );
                                  })}
                            </tr>
                          );
                        })}
                        {viewMode === 'categoria' && colunasMesesCategoria.length > 0 && (
                          <tr className="bg-[color:var(--surface-muted)] border-t border-[color:var(--border)]">
                            <td className="px-3 py-3 text-xs sm:text-sm text-[color:var(--text-primary)] sticky left-0 z-10 bg-[color:var(--surface-muted)] whitespace-nowrap">
                              <div className="font-semibold whitespace-nowrap">Total Categoria</div>
                            </td>
                            {colunasCategorias.map((categoria) => {
                              const totalCategoria = totaisCategoria.totalPorCategoria.get(categoria) || 0;
                              return (
                                <td
                                  key={`total-cat-${categoria}`}
                                  className="px-3 py-3 text-right text-xs sm:text-sm whitespace-nowrap text-[color:var(--text-primary)] font-semibold"
                                >
                                  {formatBRL(totalCategoria)}
                                </td>
                              );
                            })}
                            <td className="px-3 py-3 text-right text-xs sm:text-sm whitespace-nowrap text-[color:var(--text-primary)] font-semibold">
                              {formatBRL(totaisCategoria.totalGeral)}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {parcelamentosFiltradosPorCard.length === 0 && (
                    <div className="text-sm text-[color:var(--text-muted)]">Nenhuma conta encontrada para o card selecionado.</div>
                  )}
                </div>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ParcelamentosMovfit;
